import { useEffect, useMemo, useState } from "react";
import CategoryTag from "@/components/base/CategoryTag";
import SectionSkeleton from "@/components/base/SectionSkeleton";
import SEO from "@/components/SEO";
import { canonical, collectionPageSchema } from "@/utils/seo";
import { CONTENT_TYPE_META } from "@/config/brand";
import { getAllPosts } from "@/api/content";
import type { PostCard } from "@/types/content";
import type { ContentType } from "@/config/brand";

const PAGE_SIZE = 9;

const CONTENT_TYPE_ORDER: ContentType[] = [
  "multiShopReview",
  "singleShopReview",
  "guide",
  "lazyPack",
  "story",
];

export default function ArticlesPage({ initialData }: { initialData?: PostCard[] }) {
  const [posts, setPosts] = useState<PostCard[]>(initialData || []);
  const [loading, setLoading] = useState(!initialData || initialData.length === 0);
  const [activeCategory, setActiveCategory] = useState<string>("全部");
  const [activeContentType, setActiveContentType] = useState<ContentType | "全部">("全部");
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<"newest" | "rating">("newest");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    getAllPosts().then((data) => {
      setPosts(data);
      setLoading(false);
    });
  }, []);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [activeCategory, activeContentType, activeTag, sortBy]);

  // Extract unique categories and content types
  const categories = useMemo(() => {
    const cats = new Set(posts.map((p) => p.category));
    return ["全部", ...Array.from(cats)];
  }, [posts]);

  const contentTypes = useMemo(() => {
    const types = new Set<ContentType>();
    posts.forEach((p) => {
      if (p.contentType) types.add(p.contentType);
    });
    return ["全部", ...CONTENT_TYPE_ORDER.filter((t) => types.has(t))] as (ContentType | "全部")[];
  }, [posts]);

  const allTags = useMemo(() => {
    const tagMap = new Map<string, number>();
    posts.forEach((p) => {
      p.tags.forEach((t) => tagMap.set(t, (tagMap.get(t) || 0) + 1));
    });
    return Array.from(tagMap.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 12)
      .map(([tag]) => tag);
  }, [posts]);

  // Filter and sort
  const filteredPosts = useMemo(() => {
    let result = [...posts];

    if (activeCategory !== "全部") {
      result = result.filter((p) => p.category === activeCategory);
    }

    if (activeContentType !== "全部") {
      result = result.filter((p) => p.contentType === activeContentType);
    }

    if (activeTag) {
      result = result.filter((p) => p.tags.includes(activeTag));
    }

    if (sortBy === "rating") {
      result.sort((a, b) => (b.rating || 0) - (a.rating || 0));
    }
    // newest is default order from API

    return result;
  }, [posts, activeCategory, activeContentType, activeTag, sortBy]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredPosts.length / PAGE_SIZE));
  const paginatedPosts = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredPosts.slice(start, start + PAGE_SIZE);
  }, [filteredPosts, currentPage]);

  const pageNumbers = useMemo(() => {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, "...", totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, "...", totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages);
      }
    }
    return pages;
  }, [currentPage, totalPages]);

  if (loading) {
    return <SectionSkeleton type="spinner" />;
  }

  return (
    <div className="w-full">
      <SEO
        meta={{
          title: "最新文章｜美好時光指南",
          description: "神秘客實測、懶人包整理、完整評比，幫你找到最棒的體驗與最優質的店家。",
          canonical: canonical("/articles"),
          keywords: ["最新文章", "美業探店", "懶人包", "評比", "美好時光指南"],
          structuredData: collectionPageSchema("最新文章｜美好時光指南", "神秘客實測、懶人包整理、完整評比。", "/articles"),
        }}
      />
      {/* Header */}
      <div className="w-full px-4 md:px-8 lg:px-12 pt-12 md:pt-16 pb-6">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-800 mb-3">
            最新文章
          </h1>
          <p className="text-slate-500 max-w-2xl">
            神秘客實測、懶人包整理、完整評比，幫你找到最棒的體驗
          </p>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="w-full border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-4 space-y-3">
          {/* Categories */}
          <div className="flex items-center gap-3 overflow-x-auto pb-1">
            <span className="text-sm font-medium text-slate-400 flex-shrink-0">
              分類：
            </span>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => {
                  setActiveCategory(cat);
                  setActiveTag(null);
                }}
                className={`px-3.5 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  activeCategory === cat
                    ? "bg-primary text-white"
                    : "bg-slate-50 text-slate-600 hover:bg-slate-100"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Content Types */}
          <div className="flex items-center gap-3 overflow-x-auto pb-1">
            <span className="text-sm font-medium text-slate-400 flex-shrink-0">
              類型：
            </span>
            {contentTypes.map((type) => {
              const meta = type === "全部" ? null : CONTENT_TYPE_META[type];
              const isActive = activeContentType === type;
              return (
                <button
                  key={type}
                  onClick={() => setActiveContentType(type)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    isActive
                      ? "bg-primary text-white"
                      : meta
                        ? `${meta.badgeClass} hover:opacity-80`
                        : "bg-slate-50 text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  {type === "全部" ? "全部" : meta?.shortLabel || type}
                </button>
              );
            })}
          </div>

          {/* Tags + Sort */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2 overflow-x-auto pb-1">
              <span className="text-sm font-medium text-slate-400 flex-shrink-0">
                標籤：
              </span>
              {allTags.map((tag) => (
                <button
                  key={tag}
                  onClick={() =>
                    setActiveTag(activeTag === tag ? null : tag)
                  }
                  className={`px-2.5 py-1 rounded-full text-xs whitespace-nowrap transition-colors cursor-pointer ${
                    activeTag === tag
                      ? "bg-primary/10 text-primary border border-primary/20"
                      : "bg-slate-50 text-slate-500 border border-slate-100 hover:bg-slate-100"
                  }`}
                >
                  #{tag}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-sm text-slate-400">排序：</span>
              <select
                value={sortBy}
                onChange={(e) =>
                  setSortBy(e.target.value as "newest" | "rating")
                }
                className="text-sm bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-slate-700 focus:outline-none focus:border-primary cursor-pointer"
              >
                <option value="newest">最新發布</option>
                <option value="rating">最高評分</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Results Count */}
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-4">
        <p className="text-sm text-slate-400">
          共 {filteredPosts.length} 篇文章
          {activeCategory !== "全部" && (
            <span className="ml-1">
              · 分類「{activeCategory}」
            </span>
          )}
          {activeContentType !== "全部" && (
            <span className="ml-1">
              · 類型「{CONTENT_TYPE_META[activeContentType].shortLabel}」
            </span>
          )}
          {activeTag && (
            <span className="ml-1">
              · 標籤「{activeTag}」
            </span>
          )}
          <span className="ml-1">· 第 {currentPage} / {totalPages} 頁</span>
        </p>
      </div>

      {/* Grid */}
      <div className="max-w-6xl mx-auto px-4 md:px-8 pb-8 md:pb-12">
        {filteredPosts.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 text-slate-300">
              <i className="ri-search-line text-2xl" />
            </div>
            <p className="text-slate-500">沒有符合條件的文章</p>
            <button
              onClick={() => {
                setActiveCategory("全部");
                setActiveContentType("全部");
                setActiveTag(null);
                setCurrentPage(1);
              }}
              className="mt-2 text-primary text-sm hover:underline cursor-pointer"
            >
              清除篩選
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {paginatedPosts.map((post) => {
              const ctMeta = post.contentType ? CONTENT_TYPE_META[post.contentType] : null;
              return (
                <a
                  key={post.id}
                  href={`/articles/${post.slug}`}
                  className="group block bg-white rounded-xl overflow-hidden border border-slate-100 hover:border-primary/20 transition-all"
                >
                  <div className="relative w-full h-48 overflow-hidden">
                    <img
                      loading="lazy"
                      src={post.coverImage}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3 flex gap-2">
                      <CategoryTag label={post.category} variant="light" />
                      {ctMeta && (
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${ctMeta.badgeClass}`}>
                          {ctMeta.shortLabel}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="p-5">
                    <h2 className="text-lg font-semibold text-slate-800 mb-2 group-hover:text-primary transition-colors line-clamp-2">
                      {post.title}
                    </h2>
                    <p className="text-sm text-slate-500 mb-3 line-clamp-2">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-slate-400">
                      <span className="flex items-center gap-1">
                        <div className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-calendar-line" />
                        </div>
                        {post.publishedAt}
                      </span>
                      <span className="flex items-center gap-1">
                        <div className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-time-line" />
                        </div>
                        {post.readingTime} 分鐘
                      </span>
                      {post.rating && (
                        <span className="flex items-center gap-1">
                          <div className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className="ri-star-fill text-amber-400" />
                          </div>
                          {post.rating}
                        </span>
                      )}
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination */}
      {filteredPosts.length > 0 && totalPages > 1 && (
        <div className="max-w-6xl mx-auto px-4 md:px-8 pb-12 md:pb-16">
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-s-line" />
            </button>

            {pageNumbers.map((num, idx) =>
              num === "..." ? (
                <span key={`ellipsis-${idx}`} className="px-1 text-slate-400 text-sm">
                  ...
                </span>
              ) : (
                <button
                  key={num}
                  onClick={() => setCurrentPage(num as number)}
                  className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                    currentPage === num
                      ? "bg-primary text-white"
                      : "border border-slate-200 text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  {num}
                </button>
              )
            )}

            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-s-line" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}